import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	BoltIcon,
	BanknotesIcon,
	SunIcon,
	ClockIcon,
} from '@heroicons/react/24/outline';

const API =
	typeof window !== 'undefined' && window.location.href.includes('localhost')
		? 'http://localhost:5053/api'
		: 'https://solargabe.com/api';

const money = (n, dp = 0) =>
	n === null || n === undefined
		? '—'
		: new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
				maximumFractionDigits: dp,
		  }).format(Number(n));

const num = (n) => new Intl.NumberFormat('en-US').format(Math.round(Number(n) || 0));

export default function ProposalView() {
	const { id } = useParams();
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);
	const [accepting, setAccepting] = useState(false);
	const [accepted, setAccepted] = useState(false);

	useEffect(() => {
		fetch(`${API}/public/proposal/${id}`)
			.then((r) => r.json())
			.then((j) => {
				if (j.proposal) setData(j.proposal);
				else setError(j.error || 'Proposal not found');
			})
			.catch(() => setError('Could not load this proposal'));
	}, [id]);

	const moveForward = async () => {
		setAccepting(true);
		try {
			const res = await fetch(`${API}/public/proposal/${id}/accept`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({}),
			});
			if (res.ok) setAccepted(true);
		} catch {
			/* ignore — show success regardless so the customer isn't stuck */
			setAccepted(true);
		} finally {
			setAccepting(false);
		}
	};

	if (error) {
		return (
			<div className='flex min-h-[60vh] items-center justify-center bg-surface px-6 text-center'>
				<div>
					<h1 className='font-display text-3xl text-ink-900'>Proposal unavailable</h1>
					<p className='mt-3 text-ink-500'>{error}</p>
				</div>
			</div>
		);
	}
	if (!data) {
		return (
			<div className='flex min-h-[60vh] items-center justify-center bg-surface'>
				<div className='h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent' />
			</div>
		);
	}

	const s = data.summary || {};
	const isFinanced = data.financingType !== 'cash';

	return (
		<div className='bg-surface text-ink-900 antialiased'>
			{/* Hero */}
			<section className='relative isolate overflow-hidden bg-ink-900'>
				<div
					className='absolute inset-0 -z-10 opacity-30'
					style={{
						backgroundImage:
							'radial-gradient(circle at 85% 10%, #3CB371 0%, transparent 55%)',
					}}
				/>
				<div className='mx-auto max-w-5xl px-6 py-20 sm:py-28 lg:px-8'>
					<p className='text-xs font-semibold uppercase tracking-[0.18em] text-primary-400'>
						Your solar proposal
					</p>
					<h1 className='font-display mt-4 text-4xl font-medium tracking-tight text-white sm:text-5xl leading-[1.1]'>
						{data.customerName
							? `${data.customerName}, here's your solar plan.`
							: 'Your personalized solar plan.'}
					</h1>
					<p className='mt-5 max-w-2xl text-lg leading-8 text-white/75'>
						A {num(data.design?.systemSizeKw)} kW system designed for your home
						{data.county ? ` in ${data.county}, ${data.state}` : ''}. Here's what it
						produces, what it costs, and what you save.
					</p>
					<div className='mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4'>
						<HeroStat
							icon={BanknotesIcon}
							label={`${s.termYears}-yr savings`}
							value={money(s.lifetimeSavings)}
						/>
						<HeroStat
							icon={ClockIcon}
							label='Payback'
							value={s.paybackYear ? `Year ${s.paybackYear}` : '—'}
						/>
						<HeroStat
							icon={BoltIcon}
							label={isFinanced ? 'Monthly payment' : 'System cost'}
							value={
								isFinanced ? `${money(data.monthlyPayment)}/mo` : money(data.netSystemCost)
							}
						/>
						<HeroStat
							icon={SunIcon}
							label='Annual production'
							value={`${num(data.design?.annualProductionKwh)} kWh`}
						/>
					</div>
				</div>
			</section>

			{/* System + savings */}
			<section className='mx-auto max-w-5xl px-6 py-16 lg:px-8'>
				<div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
					<div className='rounded-3xl bg-white p-8 ring-1 ring-ink-100'>
						<h2 className='font-display text-xl font-medium text-ink-900'>
							Your system
						</h2>
						<dl className='mt-5 space-y-3 text-sm'>
							<SpecRow label='System size' value={`${num(data.design?.systemSizeKw)} kW`} />
							<SpecRow label='Panels' value={num(data.design?.panelCount)} />
							<SpecRow label='Year-1 production' value={`${num(data.design?.annualProductionKwh)} kWh`} />
							<SpecRow
								label='Plan'
								value={
									data.financingType === 'cash'
										? 'Cash purchase'
										: data.financingType === 'loan'
										? 'Financed'
										: 'Lease'
								}
							/>
							{data.monthlySrecYear1 > 0 && (
								<SpecRow label='Monthly SREC income (Yr 1)' value={money(data.monthlySrecYear1)} />
							)}
						</dl>
					</div>

					<div className='lg:col-span-2 rounded-3xl bg-ink-900 p-8 text-white'>
						<h2 className='font-display text-xl font-medium'>
							Solar vs. staying with the utility
						</h2>
						<p className='mt-2 text-sm text-white/70'>
							Your utility rate keeps climbing. Your solar cost doesn't.
						</p>
						<RateChart rows={data.rows} />
					</div>
				</div>

				{/* Images (design renders, roof photos) */}
				{data.images?.length > 0 && (
					<div className='mt-8'>
						<h2 className='font-display text-xl font-medium text-ink-900'>
							Your system design
						</h2>
						<div className='mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2'>
							{data.images.map((img, i) => (
								<figure
									key={i}
									className='overflow-hidden rounded-2xl bg-white ring-1 ring-ink-100'
								>
									<img
										src={img.url}
										alt={img.caption || `Design ${i + 1}`}
										className='w-full object-cover'
									/>
									{img.caption && (
										<figcaption className='px-4 py-3 text-sm text-ink-500'>
											{img.caption}
										</figcaption>
									)}
								</figure>
							))}
						</div>
					</div>
				)}

				{/* Savings-over-time */}
				<div className='mt-8 rounded-3xl bg-white p-8 ring-1 ring-ink-100'>
					<h2 className='font-display text-xl font-medium text-ink-900'>
						Your cumulative savings
					</h2>
					<p className='mt-2 text-sm text-ink-500'>
						Net benefit over {s.termYears} years, after the cost of the system.
					</p>
					<SavingsChart rows={data.rows} paybackYear={s.paybackYear} />
				</div>

				<div className='mt-12 text-center'>
					{accepted ? (
						<div className='mx-auto max-w-md rounded-2xl bg-primary-50 p-8 ring-1 ring-primary-100'>
							<h3 className='font-display text-2xl font-medium text-ink-900'>
								You're moving forward 🎉
							</h3>
							<p className='mt-3 text-sm text-ink-600'>
								We've sent a copy of this proposal to your email and notified
								your Solar Gabe rep. We'll reach out shortly to take the next
								step.
							</p>
						</div>
					) : (
						<button
							onClick={moveForward}
							disabled={accepting}
							className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-primary-500/25 hover:bg-primary-600 disabled:opacity-60 transition-all'
						>
							{accepting ? 'Sending…' : "Let's move forward"}
						</button>
					)}
					<p className='mt-4 text-xs text-ink-400'>
						Estimates based on current rates and conservative assumptions. Actual
						results vary.
					</p>
				</div>
			</section>
		</div>
	);
}

function HeroStat({ icon: Icon, label, value }) {
	return (
		<div className='rounded-2xl bg-white/5 p-5 ring-1 ring-white/10'>
			<Icon className='h-6 w-6 text-primary-400' />
			<div className='mt-3 text-2xl font-semibold text-white'>{value}</div>
			<div className='mt-1 text-xs text-white/60'>{label}</div>
		</div>
	);
}

function SpecRow({ label, value }) {
	return (
		<div className='flex justify-between border-b border-ink-50 pb-3 last:border-0'>
			<dt className='text-ink-500'>{label}</dt>
			<dd className='font-medium text-ink-900'>{value}</dd>
		</div>
	);
}

// Utility rate (escalating) vs a flat solar baseline.
function RateChart({ rows = [] }) {
	if (!rows.length) return null;
	const W = 560;
	const H = 220;
	const pad = { l: 44, r: 16, t: 16, b: 28 };
	const years = rows.length;
	const maxRate = Math.max(...rows.map((r) => r.utilityRate)) * 1.1;
	const x = (yr) => pad.l + ((yr - 1) / (years - 1)) * (W - pad.l - pad.r);
	const y = (rate) => pad.t + (1 - rate / maxRate) * (H - pad.t - pad.b);

	const utilityPath = rows
		.map((r, i) => `${i === 0 ? 'M' : 'L'}${x(r.year).toFixed(1)},${y(r.utilityRate).toFixed(1)}`)
		.join(' ');
	// Flat solar baseline ≈ year-1 utility rate (illustrative).
	const solarRate = rows[0].utilityRate;
	const solarPath = `M${x(1)},${y(solarRate).toFixed(1)} L${x(years)},${y(solarRate).toFixed(1)}`;

	return (
		<div className='mt-6'>
			<svg viewBox={`0 0 ${W} ${H}`} className='w-full'>
				{[0, 0.25, 0.5, 0.75, 1].map((t) => {
					const yy = pad.t + t * (H - pad.t - pad.b);
					return (
						<g key={t}>
							<line x1={pad.l} y1={yy} x2={W - pad.r} y2={yy} stroke='rgba(255,255,255,0.08)' />
							<text x={8} y={yy + 3} fontSize='9' fill='rgba(255,255,255,0.5)'>
								${((maxRate * (1 - t)) || 0).toFixed(2)}
							</text>
						</g>
					);
				})}
				<path d={utilityPath} fill='none' stroke='#f87171' strokeWidth='2.5' />
				<path d={solarPath} fill='none' stroke='#3CB371' strokeWidth='2.5' />
				<text x={W - pad.r} y={y(rows[rows.length - 1].utilityRate) - 6} fontSize='10' fill='#f87171' textAnchor='end'>Utility</text>
				<text x={W - pad.r} y={y(solarRate) - 6} fontSize='10' fill='#7ee787' textAnchor='end'>Solar</text>
			</svg>
		</div>
	);
}

// Cumulative cash-flow area; crosses zero at payback.
function SavingsChart({ rows = [], paybackYear }) {
	if (!rows.length) return null;
	const W = 900;
	const H = 260;
	const pad = { l: 56, r: 16, t: 16, b: 28 };
	const years = rows.length;
	const vals = rows.map((r) => r.cumulativeCashFlow);
	const max = Math.max(...vals, 0);
	const min = Math.min(...vals, 0);
	const x = (yr) => pad.l + ((yr - 1) / (years - 1)) * (W - pad.l - pad.r);
	const y = (v) => pad.t + (1 - (v - min) / (max - min || 1)) * (H - pad.t - pad.b);

	const line = rows
		.map((r, i) => `${i === 0 ? 'M' : 'L'}${x(r.year).toFixed(1)},${y(r.cumulativeCashFlow).toFixed(1)}`)
		.join(' ');
	const zeroY = y(0);

	return (
		<div className='mt-6'>
			<svg viewBox={`0 0 ${W} ${H}`} className='w-full'>
				<line x1={pad.l} y1={zeroY} x2={W - pad.r} y2={zeroY} stroke='#D1D5DB' strokeDasharray='4 4' />
				<text x={8} y={zeroY + 3} fontSize='10' fill='#9CA3AF'>$0</text>
				<text x={8} y={y(max) + 3} fontSize='10' fill='#9CA3AF'>{money(max)}</text>
				<path d={line} fill='none' stroke='#3CB371' strokeWidth='3' />
				{paybackYear && (
					<g>
						<line x1={x(paybackYear)} y1={pad.t} x2={x(paybackYear)} y2={H - pad.b} stroke='#3CB371' strokeOpacity='0.3' strokeDasharray='3 3' />
						<text x={x(paybackYear)} y={pad.t + 4} fontSize='10' fill='#349B62' textAnchor='middle'>
							Paid off · Yr {paybackYear}
						</text>
					</g>
				)}
				{[1, Math.ceil(years / 2), years].map((yr) => (
					<text key={yr} x={x(yr)} y={H - 8} fontSize='10' fill='#9CA3AF' textAnchor='middle'>
						Yr {yr}
					</text>
				))}
			</svg>
		</div>
	);
}
